import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import '../styles/fonts.css';
import '../styles/races.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getRaceDetails, getChampionshipPointsConfig, startProtestOAuth } from '../services/api';
import DualScrollTable from '../components/DualScrollTable';

function formatLapTime(tenThousandths) {
  if (!tenThousandths || tenThousandths <= 0) return '--';
  const totalSeconds = tenThousandths / 10000;
  const minutes = Math.floor(totalSeconds / 60);
  const remainSecs = totalSeconds % 60;
  const secStr = remainSecs.toFixed(3);
  const paddedSecs = minutes > 0 ? secStr.padStart(6, '0') : secStr;
  return minutes > 0 ? `${minutes}:${paddedSecs}` : secStr;
}

function toEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|live\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const ttMatch = url.match(/twitch\.tv\/([a-zA-Z0-9_]+)/);
  if (ttMatch) return `https://player.twitch.tv/?channel=${ttMatch[1]}&parent=${window.location.hostname}`;
  return null;
}

function formatInterval(classInterval, finishPosition, lapsLabel) {
  if (finishPosition === 0) return '--';
  if (classInterval < 0) return `${Math.abs(classInterval)} ${lapsLabel}`;
  return `+${formatLapTime(classInterval)}`;
}

const WIND_DIR_LABEL = ['N', 'NE', 'L', 'SE', 'S', 'SO', 'O', 'NO'];

function formatRaceDuration(laps, avgLap) {
  if (!laps || !avgLap || avgLap <= 0) return '--';
  const totalSeconds = Math.round((laps * avgLap) / 10000);
  if (totalSeconds <= 0) return '--';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function formatTemp(value, units) {
  if (value == null) return '--';
  return `${value}${units === 0 ? '°F' : '°C'}`;
}

function formatWind(value, units, dir) {
  if (value == null) return '--';
  const unitStr = units === 0 ? 'mph' : 'km/h';
  const dirStr = dir != null ? (WIND_DIR_LABEL[dir] ?? '') : '';
  return `${value} ${unitStr}${dirStr ? ` ${dirStr}` : ''}`;
}

function countryFlag(code) {
  if (!code || code.length !== 2) return '';
  const offset = 127397;
  return (
    String.fromCodePoint(code.toUpperCase().charCodeAt(0) + offset) +
    String.fromCodePoint(code.toUpperCase().charCodeAt(1) + offset)
  );
}

function calcRacePoints(results, config, driverCategoriesMap, useClassPosition = false) {
  if (!config || !config.custom_points || Object.keys(config.custom_points).length === 0) return null;
  const cp = config.custom_points;
  const cb = config.custom_bonus || {};
  const maxLapsLed = Math.max(...results.map(r => r.laps_led || 0), 0);
  const map = {};

  if (driverCategoriesMap && Object.keys(driverCategoriesMap).length > 0) {
    const byCategory = {};
    results.forEach(r => {
      const cat = driverCategoriesMap[String(r.cust_id)];
      if (!cat) return;
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(r);
    });
    Object.values(byCategory).forEach(catResults => {
      catResults.sort((a, b) => a.finish_position - b.finish_position);
      catResults.forEach((r, idx) => {
        const base = parseFloat(cp[String(idx + 1)] || 0);
        let bonus = 0;
        if (r.starting_position === 0) bonus += parseFloat(cb.pole || 0);
        if (maxLapsLed > 0 && (r.laps_led || 0) === maxLapsLed) bonus += parseFloat(cb.most_laps_led || 0);
        if ((r.laps_led || 0) > 0) bonus += parseFloat(cb.any_lap_led || 0);
        const penalty = parseFloat(r.penalty_points || 0);
        map[r.cust_id] = { base, bonus, penalty, total: base + bonus - penalty };
      });
    });
  } else if (useClassPosition) {
    const byClass = {};
    results.forEach(r => {
      const cls = r.car_class_name || '';
      if (!byClass[cls]) byClass[cls] = [];
      byClass[cls].push(r);
    });
    Object.values(byClass).forEach(clsResults => {
      clsResults.sort((a, b) => (a.finish_position_in_class ?? a.finish_position) - (b.finish_position_in_class ?? b.finish_position));
      clsResults.forEach((r, idx) => {
        const base = parseFloat(cp[String(idx + 1)] || 0);
        let bonus = 0;
        if (r.starting_position === 0) bonus += parseFloat(cb.pole || 0);
        if (maxLapsLed > 0 && (r.laps_led || 0) === maxLapsLed) bonus += parseFloat(cb.most_laps_led || 0);
        if ((r.laps_led || 0) > 0) bonus += parseFloat(cb.any_lap_led || 0);
        const penalty = parseFloat(r.penalty_points || 0);
        map[r.cust_id] = { base, bonus, penalty, total: base + bonus - penalty };
      });
    });
  } else {
    results.forEach(r => {
      const base = parseFloat(cp[String(r.finish_position + 1)] || 0);
      let bonus = 0;
      if (r.starting_position === 0) bonus += parseFloat(cb.pole || 0);
      if (maxLapsLed > 0 && (r.laps_led || 0) === maxLapsLed) bonus += parseFloat(cb.most_laps_led || 0);
      if ((r.laps_led || 0) > 0) bonus += parseFloat(cb.any_lap_led || 0);
      const penalty = parseFloat(r.penalty_points || 0);
      map[r.cust_id] = { base, bonus, penalty, total: base + bonus - penalty };
    });
  }
  return Object.keys(map).length > 0 ? map : null;
}

function fmtPts(n) {
  if (n === undefined || n === null) return '--';
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(1);
}

function OverallTable({ simsessionNames, racePoints, allResults, driverCategories, driverTeams, hasMultipleCategories }) {
  const { t } = useTranslation();
  // Build one combined row per driver
  const driverMap = {};
  allResults.forEach(r => {
    if (!driverMap[r.cust_id]) {
      driverMap[r.cust_id] = {
        cust_id: r.cust_id,
        display_name: r.display_name,
        country_code: r.country_code,
        heatPts: {},
        totalBase: 0,
        totalBonus: 0,
        penalty: 0,
      };
    }
  });
  simsessionNames.forEach(simName => {
    const simPts = racePoints?.[simName] || {};
    Object.entries(simPts).forEach(([custId, pts]) => {
      const id = parseInt(custId);
      if (!driverMap[id]) return;
      driverMap[id].heatPts[simName] = pts.base || 0;
      driverMap[id].totalBase += pts.base || 0;
      driverMap[id].totalBonus += pts.bonus || 0;
      // penalty is stored identically on every heat result; take max to count once
      driverMap[id].penalty = Math.max(driverMap[id].penalty, pts.penalty || 0);
    });
  });
  const rows = Object.values(driverMap)
    .map(d => ({ ...d, total: d.totalBase + d.totalBonus - d.penalty }))
    .sort((a, b) => b.total - a.total);

  return (
    <DualScrollTable>
      <table className="results-table">
        <thead>
          <tr>
            <th>{t('common.pos')}</th>
            <th title={t('raceDetail.country')}>{t('raceDetail.country')}</th>
            {hasMultipleCategories && <th>{t('raceDetail.category')}</th>}
            <th className="col-left">{t('common.driver')}</th>
            <th className="col-team">{t('common.team')}</th>
            {simsessionNames.map(sn => <th key={sn}>{sn}</th>)}
            <th title={t('common.points')}>{t('common.points')}</th>
            <th title={t('common.bonus')}>{t('common.bonus')}</th>
            <th title={t('common.penalty')}>{t('common.penalty')}</th>
            <th>{t('common.total')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d, idx) => (
            <tr key={d.cust_id}>
              <td className={idx === 0 ? 'pos-winner' : ''}>{idx + 1}º</td>
              <td className="col-flag" title={d.country_code}>{countryFlag(d.country_code)}</td>
              {hasMultipleCategories && <td className="col-category">{driverCategories[String(d.cust_id)] || '--'}</td>}
              <td className="col-left">{d.display_name}</td>
              <td className="col-team">{driverTeams[String(d.cust_id)] || '--'}</td>
              {simsessionNames.map(sn => (
                <td key={sn}>{d.heatPts[sn] != null ? fmtPts(d.heatPts[sn]) : '--'}</td>
              ))}
              <td>{fmtPts(d.totalBase)}</td>
              <td>{fmtPts(d.totalBonus)}</td>
              <td className="col-penalty">{d.penalty > 0 ? `-${fmtPts(d.penalty)}` : '--'}</td>
              <td><strong>{fmtPts(d.total)}</strong></td>
            </tr>
          ))}
        </tbody>
      </table>
    </DualScrollTable>
  );
}

function ResultsTable({ rows, isMultiClass, racePoints, driverCategories, driverTeams, hasMultipleCategories, showPenalty = true, showBonus = true }) {
  const { t } = useTranslation();
  return (
    <DualScrollTable>
      <table className="results-table">
        <thead>
          <tr>
            <th>{t('common.pos')}</th>
            <th>{t('championshipDetail.grid')}</th>
            <th className="col-car-name">{t('raceDetail.car')}</th>
            <th>#</th>
            <th title={t('raceDetail.country')}>{t('raceDetail.country')}</th>
            {hasMultipleCategories && <th>{t('raceDetail.category')}</th>}
            <th className="col-left">{t('common.driver')}</th>
            <th className="col-team">{t('common.team')}</th>
            <th>{t('raceDetail.interval')}</th>
            <th>{t('common.laps')}</th>
            <th title={t('raceDetail.lapsLed')}>{t('raceDetail.lapsLed')}</th>
            <th>{t('raceDetail.bestLap')}</th>
            <th>{t('raceDetail.avgLap')}</th>
            <th>{t('common.incidents')}</th>
            <th title={t('common.points')}>{t('common.points')}</th>
            {showBonus && <th title={t('common.bonus')}>{t('common.bonus')}</th>}
            {showPenalty && <th title={t('common.penalty')}>{t('common.penalty')}</th>}
            <th className="col-reason-out">{t('common.status')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pos = isMultiClass ? r.finish_position_in_class : r.finish_position;
            const pts = racePoints?.[r.cust_id];
            return (
              <tr key={r.cust_id}>
                <td className={pos === 0 ? 'pos-winner' : ''}>{pos + 1}º</td>
                <td>{r.starting_position != null ? r.starting_position + 1 : '--'}</td>
                <td className="col-car-name">{r.car_name || '--'}</td>
                <td className="col-car-number">{r.livery?.car_number != null ? `#${r.livery.car_number}` : '--'}</td>
                <td className="col-flag" title={r.country_code}>{countryFlag(r.country_code)}</td>
                {hasMultipleCategories && <td className="col-category">{driverCategories[String(r.cust_id)] || '--'}</td>}
                <td className="col-left">{r.display_name}</td>
                <td className="col-team">{driverTeams[String(r.cust_id)] || '--'}</td>
                <td>{formatInterval(r.class_interval, r.finish_position, t('raceDetail.laps'))}</td>
                <td>{r.laps_complete}</td>
                <td>{r.laps_led ?? '--'}</td>
                <td>{formatLapTime(r.best_lap_time)}</td>
                <td>{formatLapTime(r.average_lap)}</td>
                <td>{r.incidents}</td>
                <td>{pts ? fmtPts(pts.base) : '--'}</td>
                {showBonus && <td>{pts ? fmtPts(pts.bonus) : '--'}</td>}
                {showPenalty && <td className="col-penalty">{pts && pts.penalty > 0 ? `-${fmtPts(pts.penalty)}` : '--'}</td>}
                <td className="col-reason-out">{r.reason_out || '--'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </DualScrollTable>
  );
}

function RaceDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';
  const [race, setRace] = useState(null);

  usePageTitle(race?.track_name || null);
  useCanonicalTag();
  useMetaTags({
    title: race?.track_name ? `${race.track_name} — Race Results | Celeritas` : 'Race Results | Celeritas',
    description: 'iRacing race results and standings on Celeritas',
    url: `https://celeritas.live/races/${id}`
  });

  const SKIES_LABEL = [
    t('raceDetail.skyClear'),
    t('raceDetail.skyPartlyCloudy'),
    t('raceDetail.skyMostlyCloudy'),
    t('raceDetail.skyOvercast'),
  ];

  const [racePoints, setRacePoints] = useState(null);
  const [driverCategories, setDriverCategories] = useState({});
  const [driverTeams, setDriverTeams] = useState({});
  const [hasMultipleCategories, setHasMultipleCategories] = useState(false);
  const [categoryNames, setCategoryNames] = useState([]);
  const [activeHeatTab, setActiveHeatTab] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState(null);
  const [racePointsOverall, setRacePointsOverall] = useState(null);
  const [teamScoringUsesOverallPosition, setTeamScoringUsesOverallPosition] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [protestLoading, setProtestLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getRaceDetails(id)
      .then(data => {
        setRace(data);
        setError(null);
        if (data.championship_championship_id) {
          getChampionshipPointsConfig(data.championship_championship_id)
            .then(config => {
              const allData = data.results || [];
              const cp = config.custom_points || {};
              const firstVal = Object.values(cp)[0];
              const isNestedPoints = Object.keys(cp).length > 0 && firstVal !== null && typeof firstVal === 'object';

              const catMap = (config.scoring_by_class && Object.keys(config.driver_categories || {}).length > 0)
                ? config.driver_categories
                : null;
              const resultClassNames = [...new Set(allData.map(r => r.car_class_name).filter(Boolean))];
              const useClassPos = (config.scoring_by_class || resultClassNames.length > 1) && !catMap;

              if (isNestedPoints) {
                const bySimsession = {};
                const bySimsessionOverall = {};
                Object.entries(cp).forEach(([simName, pointsTable]) => {
                  const simResults = allData.filter(r => r.simsession_name === simName);
                  const map = calcRacePoints(simResults, { ...config, custom_points: pointsTable }, catMap, useClassPos);
                  if (map) bySimsession[simName] = map;
                  if (catMap && config.team_scoring_uses_overall_position) {
                    const mapOverall = calcRacePoints(simResults, { ...config, custom_points: pointsTable }, null, false);
                    if (mapOverall) bySimsessionOverall[simName] = mapOverall;
                  }
                });
                if (Object.keys(bySimsession).length > 0) setRacePoints(bySimsession);
                if (Object.keys(bySimsessionOverall).length > 0) setRacePointsOverall(bySimsessionOverall);
              } else {
                const customMap = calcRacePoints(allData, config, catMap, useClassPos);
                if (customMap) {
                  setRacePoints(customMap);
                  if (catMap && config.team_scoring_uses_overall_position) {
                    const mapOverall = calcRacePoints(allData, config, null, false);
                    if (mapOverall) setRacePointsOverall(mapOverall);
                  }
                } else {
                  const simNames = [...new Set(allData.map(r => r.simsession_name).filter(Boolean))];
                  if (simNames.length > 1) {
                    const nativeNested = {};
                    simNames.forEach(simName => {
                      allData.filter(r => r.simsession_name === simName).forEach(r => {
                        if (r.points != null) {
                          if (!nativeNested[simName]) nativeNested[simName] = {};
                          const penalty = r.penalty_points || 0;
                          nativeNested[simName][r.cust_id] = { base: r.points, bonus: null, penalty, total: r.points - penalty };
                        }
                      });
                    });
                    if (Object.keys(nativeNested).length > 0) setRacePoints(nativeNested);
                  } else {
                    const nativeMap = {};
                    allData.forEach(r => {
                      if (r.points != null) {
                        const penalty = r.penalty_points || 0;
                        nativeMap[r.cust_id] = { base: r.points, bonus: null, penalty, total: r.points - penalty };
                      }
                    });
                    if (Object.keys(nativeMap).length > 0) setRacePoints(nativeMap);
                  }
                }
              }
              setDriverCategories(config.driver_categories || {});
              setDriverTeams(config.driver_teams || {});
              const cats = config.category_names || [];
              setCategoryNames(cats);
              setHasMultipleCategories(cats.length >= 2);
              setTeamScoringUsesOverallPosition(!!config.team_scoring_uses_overall_position);
            })
            .catch(() => {});
        }
      })
      .catch(err => {
        console.error('Erro ao carregar corrida:', err);
        setError(t('raceDetail.error'));
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="RaceDetailPage">
      <Header />
      <main><div className="race-content"><p style={{ color: 'white', fontSize: '20px', textAlign: 'center', paddingTop: '40px' }}>{t('raceDetail.loading')}</p></div></main>
      <Footer />
    </div>
  );

  if (error || !race) return (
    <div className="RaceDetailPage">
      <Header />
      <main><div className="race-content"><p style={{ color: 'red', fontSize: '20px', textAlign: 'center', paddingTop: '40px' }}>{error || t('raceDetail.notFound')}</p></div></main>
      <Footer />
    </div>
  );

  const allResults = (race.results || []).slice().sort((a, b) => a.finish_position - b.finish_position);
  const trackLabel = [race.track_name, race.config_name].filter(Boolean).join(' — ');
  const dateFormatted = race.start_time
    ? new Date(race.start_time).toLocaleDateString(locale, {
        day: '2-digit', month: 'short', year: 'numeric'
      }).toUpperCase()
    : null;

  const simsessionNames = [...new Set(allResults.map(r => r.simsession_name).filter(Boolean))];
  const isMultiRace = simsessionNames.length > 1;

  const overallTabLabel = t('raceDetail.overallStandings');
  const showTabs = isMultiRace;
  // Always show "Classificação Geral" for multi-race (with or without categories)
  const allTabNames = showTabs ? [...simsessionNames, overallTabLabel] : [];
  const currentHeatTab = showTabs
    ? (activeHeatTab && allTabNames.includes(activeHeatTab) ? activeHeatTab : simsessionNames[0])
    : null;

  let resultGroups;
  if (isMultiRace) {
    const orderedNames = [];
    allResults.forEach(r => {
      if (r.simsession_name && !orderedNames.includes(r.simsession_name)) {
        orderedNames.push(r.simsession_name);
      }
    });
    resultGroups = orderedNames.map(simName => ({
      label: simName,
      rows: allResults
        .filter(r => r.simsession_name === simName)
        .sort((a, b) => a.finish_position - b.finish_position),
      isMultiClass: false,
    }));
  } else {
    const classNames = [...new Set(allResults.map(r => r.car_class_name).filter(Boolean))];
    const isMultiClass = classNames.length > 1;
    if (isMultiClass) {
      const sortedClasses = classNames.sort((a, b) => {
        const bestA = Math.min(...allResults.filter(r => r.car_class_name === a).map(r => r.finish_position));
        const bestB = Math.min(...allResults.filter(r => r.car_class_name === b).map(r => r.finish_position));
        return bestA - bestB;
      });
      resultGroups = sortedClasses.map(name => ({
        label: name,
        rows: allResults
          .filter(r => r.car_class_name === name)
          .sort((a, b) => a.finish_position_in_class - b.finish_position_in_class),
        isMultiClass: true,
      }));
    } else {
      resultGroups = [{ label: null, rows: allResults, isMultiClass: false }];
    }
  }

  const isSingleRaceWithCategories = !isMultiRace && hasMultipleCategories;
  const isSingleRaceMultiClass = !isMultiRace && resultGroups.length > 1 && resultGroups[0]?.isMultiClass;
  const showResultTabs = isSingleRaceWithCategories || isSingleRaceMultiClass;

  let resultTabNames = [];
  if (isSingleRaceWithCategories) {
    resultTabNames = [overallTabLabel, ...categoryNames];
  } else if (isSingleRaceMultiClass) {
    resultTabNames = resultGroups.map(g => g.label);
  }
  const currentResultTab = showResultTabs
    ? (activeResultTab && resultTabNames.includes(activeResultTab) ? activeResultTab : resultTabNames[0])
    : null;

  return (
    <div className="RaceDetailPage">
      <Header />

      <main>
        <div className="race-content">
          <section className="race-page">

            <nav className="breadcrumb">
              <Link to="/championships">{t('common.championships')}</Link> &gt;{' '}
              <Link to={`/championships/${race.championship_championship_id}`}>{race.championship_name}</Link> &gt;{' '}
              <span>{race.track_name}</span>
            </nav>

            <div className="race-top-row">
              <div className="race-info-card">

                <div className="race-info-header">
                  {race.track_logo_url && (
                    <img
                      src={race.track_logo_url}
                      alt={race.track_name}
                      className="track-logo"
                    />
                  )}
                  <div className="race-info-title">
                    <p className="race-league-sub">{race.league_name} — {race.championship_name}</p>
                    <h1>{trackLabel}</h1>
                    {dateFormatted && <p className="race-date">{dateFormatted}</p>}
                  </div>
                </div>

                <div className="race-info-divider" />

                <div className="race-meta-grid">
                  <div className="race-meta-item">
                    <span className="meta-label">{t('raceDetail.drivers')}</span>
                    <span className="meta-value">{race.num_drivers ?? '--'}</span>
                  </div>
                  <div className="race-meta-item">
                    <span className="meta-label">{t('raceDetail.leadChanges')}</span>
                    <span className="meta-value">{race.num_lead_changes ?? '--'}</span>
                  </div>
                  <div className="race-meta-item">
                    <span className="meta-label">{t('raceDetail.yellowFlags')}</span>
                    <span className="meta-value">{race.num_cautions ?? '--'}</span>
                  </div>
                  <div className="race-meta-item">
                    <span className="meta-label">{t('raceDetail.duration')}</span>
                    <span className="meta-value">{formatRaceDuration(race.event_laps_complete, race.event_average_lap)}</span>
                  </div>
                  <div className="race-meta-item">
                    <span className="meta-label">{t('common.laps')}</span>
                    <span className="meta-value">{race.event_laps_complete ?? '--'}</span>
                  </div>
                  <div className="race-meta-item">
                    <span className="meta-label">{t('raceDetail.yellowLaps')}</span>
                    <span className="meta-value">{race.num_caution_laps ?? '--'}</span>
                  </div>
                </div>

                {race.weather && (
                  <div className="race-weather-row">
                    <div className="weather-item">
                      <span className="weather-label">{t('raceDetail.weather')}</span>
                      <span className="weather-value">{SKIES_LABEL[race.weather.skies] ?? '--'}</span>
                    </div>
                    <div className="weather-item">
                      <span className="weather-label">{t('raceDetail.temperature')}</span>
                      <span className="weather-value">{formatTemp(race.weather.temp_value, race.weather.temp_units)}</span>
                    </div>
                    <div className="weather-item">
                      <span className="weather-label">{t('raceDetail.humidity')}</span>
                      <span className="weather-value">{race.weather.rel_humidity != null ? `${race.weather.rel_humidity}%` : '--'}</span>
                    </div>
                    <div className="weather-item">
                      <span className="weather-label">{t('raceDetail.wind')}</span>
                      <span className="weather-value">{formatWind(race.weather.wind_value, race.weather.wind_units, race.weather.wind_dir)}</span>
                    </div>
                  </div>
                )}

              </div>

              <div className="sidebar-box race-broadcast-card">
                {toEmbedUrl(race.broadcast_url) ? (
                  <>
                    <div className="video-wrapper">
                      <iframe
                        src={toEmbedUrl(race.broadcast_url)}
                        title={t('raceDetail.watchBroadcast')}
                        style={{ border: 'none' }}
                        allowFullScreen
                      />
                    </div>
                    <a
                      href={race.broadcast_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="broadcast-external-link"
                    >
                      ↗ {t('raceDetail.openYouTube')}
                    </a>
                  </>
                ) : race.broadcast_url ? (
                  <div className="broadcast-link-fallback">
                    <p className="broadcast-empty" style={{ paddingBottom: 0 }}>
                      {t('raceDetail.embedNotSupported')}
                    </p>
                    <a
                      href={race.broadcast_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-watch-external"
                    >
                      ↗ {t('raceDetail.watchBroadcast')}
                    </a>
                  </div>
                ) : (
                  <p className="broadcast-empty">{t('raceDetail.noBroadcast')}</p>
                )}
              </div>

              <div className="sidebar-box race-penalties-card">
                {(() => {
                  const isVerified = race.league_has_password;
                  const windowDays = race.protest_window_days;
                  const raceTime = race.start_time ? new Date(race.start_time) : null;
                  const windowEnd = raceTime && windowDays ? new Date(raceTime.getTime() + windowDays * 86400000) : null;
                  const withinWindow = windowEnd && new Date() <= windowEnd;
                  const isProtestMode = isVerified && windowDays && withinWindow;

                  const protestError = searchParams.get('protest_error');

                  async function handleProtestLogin() {
                    setProtestLoading(true);
                    try {
                      const data = await startProtestOAuth(id);
                      window.location.href = data.oauth_url;
                    } catch {
                      setProtestLoading(false);
                    }
                  }

                  return (
                    <>
                      <div className="box-header">
                        <h3>{isProtestMode ? t('protest.sectionTitle') : t('raceDetail.penalties')}</h3>
                      </div>
                      {isProtestMode ? (
                        <div style={{ padding: '4px 0' }}>
                          {windowEnd && (
                            <p className="broadcast-empty" style={{ marginBottom: 12 }}>
                              {t('protest.windowOpen')}{' '}
                              {windowEnd.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </p>
                          )}
                          {protestError === 'nao_participou' && (
                            <p style={{ color: '#e53e3e', fontSize: '0.82rem', marginBottom: 10 }}>
                              {t('protest.notParticipated')}
                            </p>
                          )}
                          {protestError && protestError !== 'nao_participou' && (
                            <p style={{ color: '#e53e3e', fontSize: '0.82rem', marginBottom: 10 }}>
                              {t('protest.authError')}
                            </p>
                          )}
                          <p className="broadcast-empty" style={{ marginBottom: 10 }}>
                            {t('protest.loginHint')}
                          </p>
                          <button
                            className="btn-primary"
                            style={{ width: '100%' }}
                            disabled={protestLoading}
                            onClick={handleProtestLogin}
                          >
                            {protestLoading ? t('common.loading') : t('protest.loginWithIRacing')}
                          </button>
                        </div>
                      ) : (
                        race.penalties && race.penalties.length > 0 ? (
                          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {race.penalties.map(p => (
                              <li key={p.id} style={{ fontSize: '0.82rem', borderLeft: '3px solid #e53e3e', paddingLeft: 10 }}>
                                <strong>{p.display_name}</strong>{' '}
                                <span style={{ color: '#e53e3e' }}>−{p.points_deducted} pts</span>
                                {p.reason && <span style={{ opacity: 0.75, display: 'block' }}>{p.reason}</span>}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="broadcast-empty">{t('raceDetail.noPenalties')}</p>
                        )
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Widget promo banner */}
            <div style={{ background: '#161b22', border: '1px solid #2d2a1e', borderRadius: 6, padding: '8px 16px', textAlign: 'center', fontSize: '0.82rem', marginBottom: 8 }}>
              <span style={{ color: '#e8c84a' }}>{t('widget.promoBanner')}{' '}</span>
              <Link to="/widget-info" style={{ color: '#e8c84a', textDecoration: 'underline', fontWeight: 700 }}>{t('widget.promoLink')}</Link>
            </div>

            {/* Multi-race + categories: tabs */}
            {showTabs ? (
              <div className="sidebar-box results-box">
                <div className="box-header" style={{ paddingBottom: 0 }}>
                  <h3>{t('raceDetail.raceResults')}</h3>
                </div>
                <div className="heat-tabs">
                  {allTabNames.map(tabName => (
                    <button
                      key={tabName}
                      className={`heat-tab-btn${currentHeatTab === tabName ? ' active' : ''}`}
                      onClick={() => setActiveHeatTab(tabName)}
                    >
                      {tabName}
                    </button>
                  ))}
                </div>
                <div className="heat-tab-content">
                  {currentHeatTab === overallTabLabel ? (
                    /* Classificação Geral: combined score across all heats */
                    hasMultipleCategories && teamScoringUsesOverallPosition && racePointsOverall ? (
                      // With categories: show each heat with overall scoring
                      simsessionNames.map(simName => {
                        const geralRows = allResults
                          .filter(r => r.simsession_name === simName)
                          .sort((a, b) => a.finish_position - b.finish_position);
                        if (geralRows.length === 0) return null;
                        return (
                          <div key={simName} className="heat-category-section">
                            <p className="heat-category-label">{simName}</p>
                            <ResultsTable
                              rows={geralRows}
                              isMultiClass={false}
                              racePoints={racePointsOverall?.[simName]}
                              driverCategories={driverCategories}
                              driverTeams={driverTeams}
                              hasMultipleCategories={true}
                            />
                          </div>
                        );
                      })
                    ) : (
                      // Without categories (or no racePointsOverall): aggregate combined scores
                      <OverallTable
                        simsessionNames={simsessionNames}
                        racePoints={racePoints}
                        allResults={allResults}
                        driverCategories={driverCategories}
                        driverTeams={driverTeams}
                        hasMultipleCategories={hasMultipleCategories}
                      />
                    )
                  ) : !hasMultipleCategories ? (
                    /* Individual heat tab, no categories */
                    <ResultsTable
                      rows={allResults.filter(r => r.simsession_name === currentHeatTab).sort((a, b) => a.finish_position - b.finish_position)}
                      isMultiClass={false}
                      racePoints={racePoints?.[currentHeatTab]}
                      driverCategories={driverCategories}
                      driverTeams={driverTeams}
                      hasMultipleCategories={false}
                      showBonus={false}
                      showPenalty={false}
                    />
                  ) : (
                    /* Individual heat tab, with categories */
                    categoryNames.map(cat => {
                      const catRows = allResults
                        .filter(r => r.simsession_name === currentHeatTab && driverCategories[String(r.cust_id)] === cat)
                        .sort((a, b) => a.finish_position - b.finish_position);
                      if (catRows.length === 0) return null;
                      return (
                        <div key={cat} className="heat-category-section">
                          <p className="heat-category-label">{cat}</p>
                          <ResultsTable
                            rows={catRows}
                            isMultiClass={false}
                            racePoints={racePoints?.[currentHeatTab]}
                            driverCategories={driverCategories}
                            driverTeams={driverTeams}
                            hasMultipleCategories={false}
                            showBonus={false}
                            showPenalty={false}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            ) : showResultTabs ? (
              /* Single race with categories or multi-class: tabs */
              <div className="sidebar-box results-box">
                <div className="box-header" style={{ paddingBottom: 0 }}>
                  <h3>{t('raceDetail.raceResults')}</h3>
                </div>
                <div className="heat-tabs">
                  {resultTabNames.map(tab => (
                    <button
                      key={tab}
                      className={`heat-tab-btn${currentResultTab === tab ? ' active' : ''}`}
                      onClick={() => setActiveResultTab(tab)}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                <div className="heat-tab-content">
                  {isSingleRaceWithCategories ? (
                    currentResultTab === overallTabLabel ? (
                      <ResultsTable
                        rows={[...allResults].sort((a, b) => a.finish_position - b.finish_position)}
                        isMultiClass={false}
                        racePoints={racePoints}
                        driverCategories={driverCategories}
                        driverTeams={driverTeams}
                        hasMultipleCategories={true}
                      />
                    ) : (
                      <ResultsTable
                        rows={allResults
                          .filter(r => driverCategories[String(r.cust_id)] === currentResultTab)
                          .sort((a, b) => a.finish_position - b.finish_position)}
                        isMultiClass={false}
                        racePoints={racePoints}
                        driverCategories={driverCategories}
                        driverTeams={driverTeams}
                        hasMultipleCategories={false}
                      />
                    )
                  ) : (
                    (() => {
                      const group = resultGroups.find(g => g.label === currentResultTab);
                      return group ? (
                        <ResultsTable
                          rows={group.rows}
                          isMultiClass={true}
                          racePoints={racePoints}
                          driverCategories={driverCategories}
                          driverTeams={driverTeams}
                          hasMultipleCategories={false}
                        />
                      ) : null;
                    })()
                  )}
                </div>
              </div>
            ) : (
              /* Single result, no tabs */
              resultGroups.map((group) => {
                const groupPoints = isMultiRace && group.label ? racePoints?.[group.label] : racePoints;
                return (
                  <div key={group.label || 'single'} className="sidebar-box results-box">
                    <div className="box-header">
                      <h3>{group.label ? `${t('raceDetail.raceResults')} — ${group.label}` : t('raceDetail.raceResults')}</h3>
                    </div>
                    <ResultsTable rows={group.rows} isMultiClass={group.isMultiClass} racePoints={groupPoints} driverCategories={driverCategories} driverTeams={driverTeams} hasMultipleCategories={hasMultipleCategories} />
                  </div>
                );
              })
            )}

          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default RaceDetail;
